/**
 * LibraryExemplarsPanel — Phase 2 wire-up for the Tender workspace's
 * Proposal & Cover tab.
 *
 * Calls GET /api/v2/library/search to pull the top-3 past proposals
 * AND the top-3 past cover letters relevant to this tender, ranked
 * by tsvector + filtered by industry/sector when known.
 *
 * Search context derivation, in priority order:
 *   1. RFP's project industry + sector (when both set, used as filters
 *      instead of full-text — most precise).
 *   2. RFP.title — usually carries client + scope keywords.
 *   3. Project's client_name + tender_portal — falls back here.
 *
 * The panel never blocks the page. If the search 500s or returns no
 * hits, the empty state explains how to seed the Library so retrieval
 * gets traction.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search, FileText, Mail, Copy, ExternalLink, RefreshCw, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { v2, v2Api } from "@/lib/v2Api";
import { downloadFile } from "@/lib/downloadFile";
import { useRfp } from "@/contexts/RfpContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";


function deriveSearchContext(rfp, project) {
  // Build a free-text query AND optional industry/sector filters.
  const industry = project?.industry || rfp?.industry || null;
  const sector = project?.sector || rfp?.sector || null;
  // Strip the file extension off the RFP title (e.g. "sample-rfp.pdf"
  // → "sample-rfp"); the search uses tsvector english so it's fine to
  // leave hyphens, but extensions add no signal.
  const titleClean = (rfp?.title || "").replace(/\.(pdf|docx?|txt|md)$/i, "");
  const parts = [
    titleClean,
    project?.client_name,
    project?.tender_portal,
  ].filter((s) => s && String(s).trim());
  const q = parts.join(" ").slice(0, 200) || "tender";
  return { q, industry, sector };
}


function ExemplarCard({ hit, downloadUrl, onCopy }) {
  return (
    <Card><CardContent className="py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-slate-800 text-sm truncate">
            {hit.title}
            {hit.client_name && (
              <span className="text-slate-500 font-normal"> · {hit.client_name}</span>
            )}
          </p>
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            <Badge variant="outline" className="text-[10px]">
              {hit.kind === "proposal" ? "Proposal" : "Cover letter"}
            </Badge>
            {hit.industry && <Badge variant="outline" className="text-[10px]">{hit.industry}</Badge>}
            {hit.sector && <Badge variant="outline" className="text-[10px]">{hit.sector}</Badge>}
            {hit.tone && <Badge variant="outline" className="text-[10px]">{hit.tone}</Badge>}
            <span className="text-[10px] text-slate-400">rank {hit.rank.toFixed(2)}</span>
          </div>
          {hit.snippet && (
            <p
              className="text-xs text-slate-600 mt-2 leading-relaxed"
              // Server returns ts_headline with <b>…</b> wrapping match
              // tokens — render those as bold without trusting wider HTML.
              dangerouslySetInnerHTML={{
                __html: hit.snippet
                  .replace(/<(?!\/?b\b)[^>]+>/gi, "")
                  .replace(/<b>/gi, "<strong>")
                  .replace(/<\/b>/gi, "</strong>"),
              }}
            />
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Button size="icon" variant="ghost" className="h-7 w-7" title="Download file"
            onClick={() => downloadFile(downloadUrl(hit.id), hit.title)
              .catch(() => toast.error("Download failed"))}>
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="icon" variant="ghost" className="h-7 w-7"
            title="Copy snippet to clipboard"
            onClick={() => onCopy(hit)}
          >
            <Copy className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </CardContent></Card>
  );
}


export default function LibraryExemplarsPanel({ rfpId }) {
  // A6: pull the RFP row from RfpContext instead of fetching it again.
  // Falls back to ``rfpId`` (the prop) when the panel is rendered outside
  // a TenderWorkspace; the rest of the panel just needs ``rfp.title`` etc.
  const { rfp, error: rfpError } = useRfp();
  const [project, setProject] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [covers, setCovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(rfpError || null);
  const [override, setOverride] = useState(""); // user-typed query

  const context = useMemo(
    () => deriveSearchContext(rfp || {}, project || {}),
    [rfp, project],
  );
  const effectiveQuery = (override.trim() || context.q).slice(0, 200);

  const runSearch = useCallback(async () => {
    if (!effectiveQuery) return;
    setLoading(true);
    setError(null);
    try {
      const params = { q: effectiveQuery, limit: 3 };
      if (context.industry) params.industry = context.industry;
      if (context.sector) params.sector = context.sector;
      const [pRes, cRes] = await Promise.all([
        v2.get("/library/search", { params: { ...params, kind: "proposal" } }),
        v2.get("/library/search", { params: { ...params, kind: "cover_letter" } }),
      ]);
      setProposals(pRes.data || []);
      setCovers(cRes.data || []);
    } catch (err) {
      const msg = err?.response?.data?.detail ?? err.message ?? "Search failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [effectiveQuery, context.industry, context.sector]);

  // Project fetch keys off the RFP from RfpContext; the panel doesn't
  // own the RFP fetch any more (collapsed by A6).
  useEffect(() => {
    if (!rfp?.project_id) return;
    let cancelled = false;
    (async () => {
      try {
        const projRes = await v2.get(`/projects/${rfp.project_id}`);
        if (!cancelled) setProject(projRes.data);
      } catch {
        /* project might be inaccessible; search will just rely on rfp.title */
      }
    })();
    return () => { cancelled = true; };
  }, [rfp?.project_id]);

  useEffect(() => {
    if (rfp) runSearch();
  }, [rfp, runSearch]);

  const copySnippet = (hit) => {
    const text = (hit.snippet || "").replace(/<[^>]+>/g, "");
    if (!text) { toast.error("No snippet to copy"); return; }
    navigator.clipboard.writeText(text).then(
      () => toast.success("Snippet copied"),
      () => toast.error("Clipboard blocked by browser"),
    );
  };

  const noHits = !loading && proposals.length === 0 && covers.length === 0;

  return (
    <Card className="border-blue-100 bg-blue-50/30">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Search className="w-4 h-4 text-blue-600" />
              Library exemplars
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Top-3 past proposals + cover letters matching this tender's
              context. Feed any of them to the LLM as starting prose.
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={runSearch} disabled={loading}>
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Input
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            placeholder={`Auto-query: "${context.q.slice(0, 60)}${context.q.length > 60 ? "…" : ""}"`}
            className="h-8 text-xs"
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
          />
          {(context.industry || context.sector) && (
            <div className="flex items-center gap-1 shrink-0">
              {context.industry && <Badge variant="outline" className="text-[10px]">{context.industry}</Badge>}
              {context.sector && <Badge variant="outline" className="text-[10px]">{context.sector}</Badge>}
            </div>
          )}
        </div>

        {error && (
          <div className="text-xs text-rose-700 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {error}
          </div>
        )}

        {loading && !proposals.length && !covers.length && (
          <p className="text-xs text-slate-500">Searching library…</p>
        )}

        {noHits && !error && (
          <Card className="border-dashed bg-white">
            <CardContent className="py-6 text-center">
              <p className="text-xs text-slate-500">
                No matching exemplars in the Library yet.
              </p>
              <p className="text-[11px] text-slate-400 mt-1">
                Upload past proposals + cover letters at
                {" "}
                <a href="/v2/library" target="_blank" rel="noreferrer"
                   className="text-blue-600 hover:underline">/v2/library</a>
                {" "} — they'll be indexed automatically and surface here next time.
              </p>
            </CardContent>
          </Card>
        )}

        {proposals.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1">
              <FileText className="w-3 h-3" /> Past proposals
            </p>
            {proposals.map((h) => (
              <ExemplarCard
                key={h.id}
                hit={h}
                downloadUrl={v2Api.library.proposalDownloadUrl}
                onCopy={copySnippet}
              />
            ))}
          </div>
        )}

        {covers.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1">
              <Mail className="w-3 h-3" /> Past cover letters
            </p>
            {covers.map((h) => (
              <ExemplarCard
                key={h.id}
                hit={h}
                downloadUrl={v2Api.library.coverLetterDownloadUrl}
                onCopy={copySnippet}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
